import { gsap, ScrollTrigger } from '../rk-gsap-core'
import { getHomeHorizontalTween } from '../rk-scroll-controller'

// Home Page Animations
export default function initializeHomeAnimations() {
  const heroSection = document.querySelector('section.hero')
  const streetsideSection = document.querySelector('section.streetside')
  const introSection = document.querySelector('section.intro')
  const registerSection = document.querySelector('section.register')
  const heroHeadline = document.querySelector('.hero-headline')
  const heroLogo = document.querySelector('.hero-logo')
  const heroRegisterButton = document.querySelector('.hero-register .register-button')
  const heroRegisterButtonLabels = document.querySelectorAll('.hero-register .register-button .button-labels span')
  const heroImage1 = document.querySelector('.panel.hero-1 .background-image')
  const heroImage2 = document.querySelector('.panel.hero-2 .background-image')
  const introBackgroundImage = document.querySelector('section.intro .background-image')
  const streetSidePanel = document.querySelector('section.streetside .panel')
  const streetSideBackgroundImage = document.querySelector('section.streetside .panel .background-image')
  const streetSideLogo = document.querySelector('.streetside-logo a')
  const registerHeadline = document.querySelector('.registration-container h2')
  const registrationForm = document.querySelector('.registration-container .form-container')

  if (!heroSection || !heroHeadline || !heroLogo || !heroRegisterButton || !heroImage1 || !heroImage2) {
    return
  }

  gsap.set(heroLogo, { autoAlpha: 0 })
  gsap.set(heroHeadline, { autoAlpha: 0 })
  gsap.set(heroRegisterButton, { autoAlpha: 0 })

  if (streetSideLogo) {
    gsap.set(streetSideLogo, { y: 100, autoAlpha: 0 })
  }

  if (registerHeadline) {
    gsap.set(registerHeadline, { autoAlpha: 0 })
  }

  if (registrationForm) {
    gsap.set(registrationForm, { autoAlpha: 0 })
  }

  // Hero Logo Animation
  gsap.to(heroLogo, {
    autoAlpha: 1,
    duration: 1,
    ease: 'power2.out'
  })

  gsap.to(heroHeadline, {
    autoAlpha: 1,
    duration: 1,
    ease: 'power2.out'
  })

  gsap.to(heroRegisterButton, {
    autoAlpha: 1,
    duration: 1,
    delay: 0.5,
    ease: 'power2.out'
  })

  gsap.to(heroRegisterButtonLabels[0], {
    marginTop: '-1.5em',
    duration: 0.5,
    ease: 'power2.out',
    delay: 3
  })

  // Registration Section Animation
  const createRegistrationAnimation = scrollTriggerConfig => {
    if (!registerHeadline || !registrationForm || !registerSection) {
      return
    }

    gsap.to([registerHeadline, registrationForm], {
      scrollTrigger: {
        trigger: registerSection,
        ...scrollTriggerConfig
      },
      autoAlpha: 1,
      duration: 0.5,
      ease: 'power2.out',
      stagger: {
        amount: 0.5,
        from: 'start'
      }
    })
  }

  const mm = gsap.matchMedia()

  // Mobile Animations
  mm.add('(max-width: 767px)', () => {
    gsap.to(heroHeadline, {
      autoAlpha: 0,
      duration: 0.5,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: heroSection,
        start: () => `top+=${window.innerWidth * 0.5} top`,
        toggleActions: 'play reverse play reverse',
        invalidateOnRefresh: true
      }
    })

    gsap.to(heroRegisterButton, {
      autoAlpha: 0,
      ease: 'power2.out',
      duration: 0.5,
      scrollTrigger: {
        trigger: heroSection,
        start: () => `top+=${window.innerWidth * 0.5} top`,
        toggleActions: 'play reverse play reverse',
        invalidateOnRefresh: true
      }
    })

    gsap.to([heroImage1, heroImage2], {
      scrollTrigger: {
        trigger: heroSection,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      },
      scale: 1.1,
      ease: 'none'
    })

    if (streetSideBackgroundImage && streetsideSection) {
      gsap.from(streetSideBackgroundImage, {
        scrollTrigger: {
          trigger: streetsideSection,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        },
        scale: 1.2,
        ease: 'none'
      })
    }

    if (streetSideLogo && streetsideSection) {
      gsap.to(streetSideLogo, {
        y: 0,
        autoAlpha: 1,
        duration: 0.75,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: streetsideSection,
          start: 'top 70%',
          toggleActions: 'play none none reverse'
        }
      })
    }

    createRegistrationAnimation({
      start: 'top 70%'
    })
  })

  // Desktop Animations
  mm.add('(min-width: 768px)', () => {
    const horizontalTween = getHomeHorizontalTween()

    if (!horizontalTween) {
      return
    }

    const horizontalTriggerBase = {
      containerAnimation: horizontalTween,
      invalidateOnRefresh: true
    }

    gsap.to(heroHeadline, {
      autoAlpha: 0,
      duration: 0.5,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: heroSection,
        start: () => `left+=${window.innerWidth * 0.5} left`,
        toggleActions: 'play reverse play reverse',
        ...horizontalTriggerBase
      }
    })

    // Hero Register Button
    gsap.to(heroRegisterButton, {
      autoAlpha: 0,
      ease: 'power2.out',
      duration: 0.5,
      scrollTrigger: {
        trigger: heroSection,
        start: () => `left+=${window.innerWidth * 0.5} left`,
        toggleActions: 'play reverse play reverse',
        ...horizontalTriggerBase
      }
    })

    // Hero Images
    gsap.to([heroImage1, heroImage2], {
      scrollTrigger: {
        trigger: heroSection,
        start: 'left right',
        end: 'right left',
        scrub: true,
        ...horizontalTriggerBase
      },
      scale: 1.5,
      ease: 'none'
    })

    if (introBackgroundImage && introSection) {
      // Intro Background Image
      gsap.fromTo(introBackgroundImage,
        {
          backgroundPosition: '100% 50%'
        },
        {
          backgroundPosition: '0% 50%',
          ease: 'none',
          scrollTrigger: {
            trigger: introSection,
            start: 'left right',
            end: 'right left',
            scrub: true,
            ...horizontalTriggerBase
          }
        }
      )
    }

    if (streetSideBackgroundImage && streetSidePanel) {
      // Streetside Background Image
      // gsap.to(streetSideBackgroundImage, {
      //   scrollTrigger: {
      //     trigger: streetSidePanel,
      //     start: 'left right',
      //     end: 'right left',
      //     scrub: true,
      //     ...horizontalTriggerBase
      //   },
      //   scale: 1.2,
      //   ease: 'none'
      // })
    }

    if (streetSideLogo && streetsideSection) {
      // Streetside Logo
      gsap.to(streetSideLogo, {
        y: 0,
        autoAlpha: 1,
        duration: 0.75,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: streetsideSection,
          start: 'left 70%',
          toggleActions: 'play none none reverse',
          ...horizontalTriggerBase
        }
      })
    }

    createRegistrationAnimation({
      start: 'left 70%',
      ...horizontalTriggerBase
    })
  })
} // initializeHomeAnimations()